/**
 * FindingsPanel — embedded on the loan-file detail page under the
 * ParsingResultsPanel.
 *
 * Shows: latest validation run status, aggregate counts by severity,
 * and the open findings grouped by UC. Polls every 5s while the loan
 * file is in `validation_in_progress`. Offers a "Re-run validation"
 * button gated on validation.rerun.
 *
 * Each finding row has a dual-path badge (both_agree / both_disagree /
 * deterministic / llm) making it easy to spot disagreements that need
 * auditor attention.
 */

import { useId, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, RefreshCw, AlertTriangle, CheckCircle2, MinusCircle, GitCompare, Download,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import { Card } from '@/acc-ui/primitives/Layout';
import { Badge } from '@/acc-ui/primitives/Badge';
import { Button } from '@/acc-ui/primitives/Button';
import { validationApi } from '@/api/validation-api';
import { usePermission } from '@/auth/use-permission';
import {
  LOAN_FILE_STATE,
  VALIDATION_RERUN,
  VERDICT_SOURCE,
  SEVERITY,
  FINDING_STATUS,
  type FindingDTO,
  type Severity,
  type VerdictSource,
} from '@loanaudit/shared';
import { exportFindingsToCSV } from '@/lib/export-findings';
import { cn } from '@/lib/utils';

// States where polling is active — validation is in flight
const NON_TERMINAL_STATES: string[] = [
  LOAN_FILE_STATE.VALIDATION_IN_PROGRESS,
  LOAN_FILE_STATE.PARSING_IN_PROGRESS,
];

// -------------------------------------------------------------------------
// Severity → tone
// -------------------------------------------------------------------------

const SEVERITY_TONE: Record<Severity, 'danger' | 'warning' | 'info' | 'neutral' | 'muted'> = {
  critical: 'danger',
  high: 'danger',
  medium: 'warning',
  low: 'info',
  info: 'muted',
};

const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Info',
};

const STATUS_LABEL: Record<string, string> = {
  open: 'Open', accepted: 'Accepted', waived: 'Waived',
  false_positive: 'False positive', superseded: 'Superseded',
};

// -------------------------------------------------------------------------
// Severity grouping — findings are bucketed by severity so the ones needing
// attention sort to the top. Two extra buckets sit outside the severity
// scale: `inconclusive` (the engine couldn't evaluate — no severity) and
// `resolved` (actioned, so no longer triage material).
// -------------------------------------------------------------------------

/** Used by the printable report, which stays grouped by use case. */
const UC_LABEL: Record<'uc1' | 'uc2' | 'uc3', string> = {
  uc1: 'Completeness',
  uc2: 'Document cross-check',
  uc3: 'CAM recompute',
};

type FindingBucket = Severity | 'inconclusive' | 'resolved';

const BUCKET_ORDER: readonly FindingBucket[] = [
  'critical', 'high', 'medium', 'low', 'info', 'inconclusive', 'resolved',
];

const BUCKET_LABEL: Record<FindingBucket, string> = {
  ...SEVERITY_LABEL,
  inconclusive: 'Inconclusive',
  resolved: 'Resolved',
};

/** Dot colour for the group header — mirrors the severity badge tone. */
const BUCKET_DOT: Record<FindingBucket, string> = {
  critical: 'bg-red-600',
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-sky-500',
  info: 'bg-slate-400',
  inconclusive: 'bg-amber-400',
  resolved: 'bg-emerald-500',
};

/** Open by default only for findings that need action. */
const BUCKET_DEFAULT_OPEN: Record<FindingBucket, boolean> = {
  critical: true, high: true, medium: true,
  low: false, info: false, inconclusive: false, resolved: false,
};

// -------------------------------------------------------------------------
// Verdict-source badge — the dual-path story, made legible
// -------------------------------------------------------------------------

export function VerdictSourceBadge({ source }: { source: VerdictSource }) {
  switch (source) {
    case VERDICT_SOURCE.BOTH_AGREE:
      return (
        <Badge tone="success" className="gap-1">
          <CheckCircle2 className="w-3 h-3" /> Both paths agree
        </Badge>
      );
    case VERDICT_SOURCE.BOTH_DISAGREE:
      return (
        <Badge tone="warning" className="gap-1">
          <GitCompare className="w-3 h-3" /> Paths disagree
        </Badge>
      );
    case VERDICT_SOURCE.LLM:
      return <Badge tone="info">LLM only</Badge>;
    case VERDICT_SOURCE.DETERMINISTIC:
    default:
      return <Badge tone="neutral">Rule only</Badge>;
  }
}

// -------------------------------------------------------------------------
// Main panel
// -------------------------------------------------------------------------

/**
 * Build a print-friendly HTML report and open it in a new tab.
 * The new tab launches the browser's print dialog automatically so the
 * auditor can save it as PDF, print it, or copy text — without us
 * needing a server-side PDF renderer for the demo.
 *
 * Once a backend export endpoint exists (Sprint 10 in the docs), this
 * client-side path stays as a fallback / preview.
 */
function downloadValidationReport(args: {
  loanFileId: string;
  findingsByUc: Record<string, FindingDTO[]>;
  counts: { bySeverity: { critical: number; high: number; medium: number; low: number; info: number } } | undefined;
  runStatus: string | undefined;
}) {
  const { loanFileId, findingsByUc, counts, runStatus } = args;
  const generatedAt = new Date().toLocaleString();
  const allFindings = (['uc1', 'uc2', 'uc3'] as const).flatMap((uc) => findingsByUc[uc] ?? []);
  const totalFindings = allFindings.length;

  const escape = (s: string) =>
    String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const severityColor: Record<string, string> = {
    critical: '#b91c1c',
    high: '#dc2626',
    medium: '#d97706',
    low: '#65a30d',
    info: '#0284c7',
  };

  const ucSections = (['uc1', 'uc2', 'uc3'] as const)
    .map((uc) => {
      const list = findingsByUc[uc] ?? [];
      if (list.length === 0) return '';
      return `
        <section>
          <h2>${uc.toUpperCase()} · ${UC_LABEL[uc]} <span class="count">${list.length} finding${list.length === 1 ? '' : 's'}</span></h2>
          <table>
            <thead>
              <tr>
                <th style="width:90px">Severity</th>
                <th>Finding</th>
                <th style="width:140px">Source</th>
                <th style="width:120px">Rule</th>
                <th style="width:90px">Confidence</th>
                <th style="width:90px">Status</th>
              </tr>
            </thead>
            <tbody>
              ${list
                .map(
                  (f) => `
                <tr>
                  <td><span class="sev" style="color:${severityColor[f.severity] ?? '#374151'}">${escape(SEVERITY_LABEL[f.severity as Severity] ?? f.severity)}</span></td>
                  <td>
                    <div class="title">${escape(f.title)}</div>
                    <div class="desc">${escape(f.description ?? '')}</div>
                  </td>
                  <td>${escape(f.verdictSource)}</td>
                  <td class="mono">${escape(f.ruleId)}</td>
                  <td>${(f.confidence * 100).toFixed(0)}%</td>
                  <td>${escape(f.status)}</td>
                </tr>`,
                )
                .join('')}
            </tbody>
          </table>
        </section>
      `;
    })
    .join('');

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Validation Report — ${escape(loanFileId)}</title>
<style>
  @page { size: A4; margin: 16mm 14mm; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; color: #0f172a; font-size: 11pt; line-height: 1.5; margin: 0; padding: 24px; }
  header { border-bottom: 2px solid #97144D; padding-bottom: 12px; margin-bottom: 18px; }
  header h1 { color: #97144D; font-size: 20pt; margin: 0 0 4px 0; }
  header .meta { color: #64748b; font-size: 10pt; }
  .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 16px 0 24px 0; }
  .summary .box { border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px 12px; }
  .summary .box .label { font-size: 9pt; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
  .summary .box .value { font-size: 18pt; font-weight: 600; color: #0f172a; margin-top: 2px; }
  section { margin: 18px 0; page-break-inside: avoid; }
  section h2 { color: #0f172a; font-size: 13pt; border-bottom: 1px solid #cbd5e1; padding-bottom: 6px; margin: 0 0 10px 0; }
  section h2 .count { font-size: 10pt; color: #64748b; font-weight: 400; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: 9pt; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; border-bottom: 1px solid #cbd5e1; padding: 6px 8px; }
  td { vertical-align: top; padding: 8px; border-bottom: 1px solid #f1f5f9; font-size: 10pt; }
  td.mono { font-family: 'SF Mono', Menlo, Consolas, monospace; font-size: 9pt; color: #475569; }
  .sev { font-weight: 600; text-transform: uppercase; font-size: 9pt; letter-spacing: 0.5px; }
  .title { font-weight: 600; color: #0f172a; }
  .desc { color: #475569; margin-top: 3px; font-size: 10pt; }
  .empty { text-align: center; padding: 40px; color: #16a34a; font-size: 12pt; }
  footer { margin-top: 30px; padding-top: 12px; border-top: 1px solid #e2e8f0; font-size: 9pt; color: #64748b; text-align: center; }
  @media print {
    body { padding: 0; }
    section { page-break-inside: avoid; }
  }
</style>
</head>
<body>
<header>
  <h1>Validation Findings Report</h1>
  <div class="meta">Loan File: ${escape(loanFileId)} · Generated ${generatedAt}${runStatus ? ` · Run status: ${escape(runStatus)}` : ''}</div>
</header>
<div class="summary">
  <div class="box"><div class="label">Total findings</div><div class="value">${totalFindings}</div></div>
  <div class="box"><div class="label">Critical</div><div class="value" style="color:#b91c1c">${counts?.bySeverity.critical ?? 0}</div></div>
  <div class="box"><div class="label">High</div><div class="value" style="color:#dc2626">${counts?.bySeverity.high ?? 0}</div></div>
  <div class="box"><div class="label">Medium</div><div class="value" style="color:#d97706">${counts?.bySeverity.medium ?? 0}</div></div>
</div>
${totalFindings === 0 ? '<div class="empty">No findings on the latest run — all checks passed.</div>' : ucSections}
<footer>Generated by Agentic Loan Audit · Applied Cloud Computing</footer>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) {
    // Popup blocked; fall back to download as .html
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `validation-report-${loanFileId}.html`;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  // Give the new tab a tick to render before invoking print
  setTimeout(() => {
    try {
      win.focus();
      win.print();
    } catch {
      /* user can use browser print menu */
    }
  }, 250);
}

export function FindingsPanel({ loanFileId }: { loanFileId: string }) {
  const qc = useQueryClient();
  const { has } = usePermission();
  const canRetrigger = has(VALIDATION_RERUN);

  const query = useQuery({
    queryKey: ['validation', 'loanFile', loanFileId],
    queryFn: () => validationApi.getLoanFileView(loanFileId),
    refetchInterval: (q) => {
      const state = q.state.data?.state;
      if (!state) return 5000;
      return NON_TERMINAL_STATES.includes(state) ? 5000 : false;
    },
  });

  const rerun = useMutation({
    mutationFn: () => validationApi.runValidation(loanFileId, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['validation', 'loanFile', loanFileId] });
      qc.invalidateQueries({ queryKey: ['loanFile', loanFileId] });
    },
  });

  // Group by severity so the things needing attention sort to the top.
  // Resolved findings leave the severity buckets entirely (they no longer
  // need action), and findings the engine couldn't evaluate — which carry
  // no severity — collect under "Inconclusive" rather than being mislabelled.
  const findingsBySeverity = useMemo(() => {
    const grouped: Record<FindingBucket, FindingDTO[]> = {
      critical: [], high: [], medium: [], low: [], info: [], inconclusive: [], resolved: [],
    };
    const data = query.data;
    if (!data) return grouped;
    for (const f of data.findings) {
      if (f.status === FINDING_STATUS.SUPERSEDED) continue;
      if (f.status !== FINDING_STATUS.OPEN) {
        grouped.resolved.push(f);
        continue;
      }
      const sev = f.severity as Severity | null | undefined;
      grouped[sev && sev in SEVERITY_LABEL ? (sev as FindingBucket) : 'inconclusive'].push(f);
    }
    return grouped;
  }, [query.data]);

  // The printable report keeps its UC grouping — it's a document laid out by
  // use case, not a triage surface, so it is deliberately unchanged by the
  // severity regrouping above.
  const findingsByUc = useMemo(() => {
    const grouped: Record<string, FindingDTO[]> = { uc1: [], uc2: [], uc3: [] };
    const data = query.data;
    if (!data) return grouped;
    for (const f of data.findings) {
      if (f.status === FINDING_STATUS.SUPERSEDED) continue;
      (grouped[f.ucId] ??= []).push(f);
    }
    return grouped;
  }, [query.data]);

  // Group open/closed state. `override` is set by Expand/Collapse all and
  // applies to every group; an individual toggle wins over it until the next
  // Expand/Collapse all. Absent both, each group falls back to its default
  // (open only when it holds findings that need attention).
  const [groupOverride, setGroupOverride] = useState<boolean | null>(null);
  const [groupToggled, setGroupToggled] = useState<Record<string, boolean>>({});
  const setAllGroups = (open: boolean) => {
    setGroupOverride(open);
    setGroupToggled({});
  };

  if (query.isLoading) {
    return (
      <Card>
        <div className="py-6 text-center text-sm text-text-muted">
          Loading validation findings…
        </div>
      </Card>
    );
  }

  const data = query.data;
  if (!data) return null;

  const latest = data.latestRun;
  const counts = latest?.counts;

  return (
    <Card padding={false}>
      <div className="px-5 py-3 flex items-center justify-between border-b border-border">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-brand-blue" />
          <span className="font-semibold text-text-primary">Validation Findings</span>
          {latest?.status === 'running' && (
            <Badge tone="info">Running…</Badge>
          )}
          {latest?.status === 'partial' && (
            <Badge tone="warning">Partial — some rules errored</Badge>
          )}
          {latest?.status === 'failed' && (
            <Badge tone="danger">Failed</Badge>
          )}
          {counts && (
            <>
              {counts.bySeverity.critical > 0 && (
                <Badge tone="danger">{counts.bySeverity.critical} critical</Badge>
              )}
              {counts.bySeverity.high > 0 && (
                <Badge tone="danger">{counts.bySeverity.high} high</Badge>
              )}
              {counts.byVerdictSource.both_disagree > 0 && (
                <Badge tone="warning" className="gap-1">
                  <GitCompare className="w-3 h-3" />
                  {counts.byVerdictSource.both_disagree} disagreement{counts.byVerdictSource.both_disagree === 1 ? '' : 's'}
                </Badge>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {latest && (
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<Download className="w-3.5 h-3.5" />}
              onClick={() =>
                downloadValidationReport({
                  loanFileId,
                  findingsByUc,
                  counts,
                  runStatus: latest.status,
                })
              }
            >
              Download report
            </Button>
          )}
          {latest && data.findings.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<Download className="w-3.5 h-3.5" />}
              onClick={() => exportFindingsToCSV(data.findings, `findings-${loanFileId.slice(0, 8)}.csv`)}
            >
              Export CSV
            </Button>
          )}
          {canRetrigger && data.canRun && (
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<RefreshCw className="w-3.5 h-3.5" />}
              onClick={() => rerun.mutate()}
              loading={rerun.isPending}
            >
              Re-run validation
            </Button>
          )}
        </div>
      </div>
      {!latest ? (
        <div className="py-8 text-center text-sm text-text-muted">
          No validation runs yet. {data.canRun ? 'Trigger one with Re-run validation.' : 'Waiting for parsing to complete.'}
        </div>
      ) : data.findings.length === 0 ? (
        <div className="py-8 flex items-center justify-center gap-2 text-sm text-emerald-700">
          <CheckCircle2 className="w-4 h-4" />
          Clean — no findings on the latest run.
        </div>
      ) : (
        <>
          <div className="px-5 py-2 flex items-center justify-end gap-2 border-b border-border">
            <button
              type="button"
              onClick={() => setAllGroups(true)}
              className="text-micro text-brand-blue hover:underline"
            >
              Expand all
            </button>
            <span className="text-micro text-text-tertiary" aria-hidden>·</span>
            <button
              type="button"
              onClick={() => setAllGroups(false)}
              className="text-micro text-brand-blue hover:underline"
            >
              Collapse all
            </button>
          </div>
          <div className="divide-y divide-border">
            {BUCKET_ORDER.map((bucket) => {
              const list = findingsBySeverity[bucket];
              if (list.length === 0) return null;
              const open =
                groupToggled[bucket] ?? groupOverride ?? BUCKET_DEFAULT_OPEN[bucket];
              return (
                <FindingGroup
                  key={bucket}
                  bucket={bucket}
                  findings={list}
                  open={open}
                  onToggle={() =>
                    setGroupToggled((t) => ({ ...t, [bucket]: !open }))
                  }
                />
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
}

/**
 * One severity group. Rendered inline rather than with the `Collapsible`
 * primitive because that primitive wraps its content in a Card, and this
 * panel is already a Card — nesting the two double-borders every group.
 * The a11y contract matches Collapsible's (button + aria-expanded/controls).
 */
function FindingGroup({
  bucket, findings, open, onToggle,
}: {
  bucket: FindingBucket;
  findings: FindingDTO[];
  open: boolean;
  onToggle: () => void;
}) {
  const bodyId = useId();
  return (
    <section>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className="w-full flex items-center gap-2 px-5 py-2.5 text-left hover:bg-slate-900/[0.03] transition-colors"
      >
        <ChevronDown
          className={cn(
            'w-4 h-4 text-text-muted shrink-0 transition-transform',
            !open && '-rotate-90',
          )}
          aria-hidden
        />
        <span className={cn('w-2 h-2 rounded-full shrink-0', BUCKET_DOT[bucket])} aria-hidden />
        <span className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
          {BUCKET_LABEL[bucket]}
        </span>
        <span className="text-caption text-text-tertiary tabular-nums">
          {findings.length}
        </span>
      </button>
      {open && (
        <ul id={bodyId} className="px-5 pb-3 space-y-1">
          {findings.map((f) => (
            <FindingRow key={f.id} finding={f} />
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * A single finding, collapsed to one line by default. A loan file can carry
 * dozens of findings; rendering every description inline turned the panel
 * into a wall of text. The summary line carries enough to triage (severity,
 * status, title) and the body opens on demand.
 */
function FindingRow({ finding }: { finding: FindingDTO }) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const severity = finding.severity as Severity | null | undefined;
  const tone = severity ? SEVERITY_TONE[severity] ?? 'neutral' : 'muted';
  const statusIcon =
    finding.status === FINDING_STATUS.OPEN ? (
      <AlertTriangle className="w-4 h-4 text-amber-600" />
    ) : finding.status === FINDING_STATUS.FALSE_POSITIVE ? (
      <MinusCircle className="w-4 h-4 text-text-muted" />
    ) : (
      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
    );
  return (
    <li className="rounded-md hover:bg-slate-50">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="w-full flex items-center gap-2 p-2 text-left"
      >
        <ChevronRight
          className={cn(
            'w-3.5 h-3.5 text-text-muted shrink-0 transition-transform',
            open && 'rotate-90',
          )}
          aria-hidden
        />
        <span className="shrink-0">{statusIcon}</span>
        <Badge tone={tone}>
          {severity ? SEVERITY_LABEL[severity] : 'Inconclusive'}
        </Badge>
        {finding.status !== FINDING_STATUS.OPEN && (
          <Badge tone="muted">{STATUS_LABEL[finding.status] ?? finding.status}</Badge>
        )}
        <span className="font-medium text-label text-text-primary truncate min-w-0">
          {finding.title}
        </span>
      </button>
      {open && (
        <div id={bodyId} className="pl-9 pr-2 pb-2.5 -mt-0.5">
          <div className="text-caption text-text-secondary leading-relaxed">
            {finding.description}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <VerdictSourceBadge source={finding.verdictSource} />
            <span className="text-micro text-text-tertiary">
              {finding.ruleId} · confidence {(finding.confidence * 100).toFixed(0)}%
            </span>
            <Link
              to={`/findings/${finding.id}`}
              className="text-micro text-brand-blue hover:underline ml-auto"
            >
              View full detail →
            </Link>
          </div>
        </div>
      )}
    </li>
  );
}

// Kept available for callers that need just severity formatting.
void SEVERITY;
